import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  inject,
  input,
  OnInit,
  signal,
  TemplateRef,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

type SelectableCardsValue = unknown[] | unknown | null;

@Component({
  selector: 'lib-selectable-cards',
  imports: [CommonModule],
  templateUrl: './selectable-cards.component.html',
  styleUrl: './selectable-cards.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectableCardsComponent<T>
  implements ControlValueAccessor, OnInit
{
  readonly items = input.required<T[]>();
  readonly multiSelect = input<boolean>(false);
  readonly itemIdKey = input.required<keyof T>();
  readonly itemTitleKey = input.required<keyof T>();
  readonly groupLabel = input.required<string>();

  @ContentChild(TemplateRef) itemTemplate?: TemplateRef<{ $implicit: T }>;

  protected readonly ngControl = inject(NgControl, {
    self: true,
    optional: true,
  });
  protected readonly isDisabled = signal(false);
  private selectedIds = new Set<unknown>();
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: SelectableCardsValue) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    // Initialize disabled state if FormControl is already disabled
    if (this.ngControl?.control?.disabled) {
      this.isDisabled.set(true);
    }
  }

  writeValue(value: SelectableCardsValue): void {
    this.selectedIds.clear();
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        for (const v of value) this.selectedIds.add(v);
      } else {
        this.selectedIds.add(value);
      }
    }
  }

  registerOnChange(fn: (value: SelectableCardsValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  isSelected(item: T): boolean {
    const id = item[this.itemIdKey()];
    return this.selectedIds.has(id);
  }

  toggleSelection(item: T): void {
    if (this.isDisabled()) {
      return;
    }

    const id = item[this.itemIdKey()];

    if (this.multiSelect()) {
      // Multi-select mode
      if (this.selectedIds.has(id)) {
        this.selectedIds.delete(id);
      } else {
        this.selectedIds.add(id);
      }
      this.onChange([...this.selectedIds]);
    } else {
      // Single-select mode
      if (this.selectedIds.has(id)) {
        this.selectedIds.clear();
        // eslint-disable-next-line unicorn/no-null
        this.onChange(null);
      } else {
        this.selectedIds.clear();
        this.selectedIds.add(id);
        this.onChange(id);
      }
    }

    this.onTouched();
  }
}
